import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import styles from "./SearchResults.module.css";

const SearchResults = () => {
  const [results, setResults] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchQuery = new URLSearchParams(location.search).get("q");

  useEffect(() => {
    fetchSearchResults(searchQuery);
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") setIsDarkMode(true);
  }, [searchQuery]);

  const decodeBuffer = (imageData) => {
    if (!imageData) return null;
    const base64Prefix = "data:image/jpeg;base64,";
    if (imageData.startsWith(base64Prefix)) {
      try {
        const base64String = imageData.slice(base64Prefix.length);
        const decoded = atob(base64String);
        console.log("Decoded image path:", decoded);
        return decoded === "null" ? null : decoded;
      } catch (error) {
        console.error("Error decoding base64 image:", error);
        return null;
      }
    }
    return imageData;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    const normalizedPath = imagePath.replace(/\\/g, "/");
    const url = `${process.env.REACT_APP_BASE_URL}/Uploads/${normalizedPath
      .split("/")
      .pop()}`;
    console.log("Image URL:", url);
    return url;
  };

  const fetchSearchResults = async (query) => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_BASE_URL
        }/api/article/search-articles?q=${encodeURIComponent(query)}`
      );
      if (response.ok) {
        const data = await response.json();
        console.log("Search results API response:", data);
        const processedResults = data.map((article) => ({
          id: article.id,
          title: article.title || "Untitled",
          description: article.description || "",
          category: article.category || article.sport_name || "Uncategorized",
          author: {
            name: article.author?.name || "Unknown Author",
            avatar: article.author?.avatar || null,
          },
          date: article.date || "Unknown Date",
          image: decodeBuffer(article.image),
        }));
        console.log("Processed results:", processedResults);
        setResults(processedResults);
      } else {
        console.error("Failed to fetch search results:", response.status);
        setResults([]);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      setResults([]);
    }
  };

  const handleArticleClick = (articleId) => {
    navigate(`/article/${articleId}`);
  };

  const handleLoginClick = () => {
    navigate("/loginContributor");
  };

  const handleRegisterClick = () => {
    navigate("/register");
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    localStorage.setItem("theme", isDarkMode ? "light" : "dark");
  };

  return (
    <div
      className={`${styles.searchResultsPage} ${
        isDarkMode ? styles.darkTheme : ""
      }`}
    >
      <HeaderMain />
      <div className={styles.authButtons}>
        <button className={styles.register} onClick={handleLoginClick}>
          Login as contributor
        </button>
        <button className={styles.register} onClick={handleRegisterClick}>
          Register as contributor
        </button>
      </div>
      <motion.button
        className={styles.mobileDarkModeToggle}
        onClick={toggleDarkMode}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
      </motion.button>
      <div className={styles.resultsContainer}>
        {results.length > 0 ? (
          results.map((result, index) => (
            <motion.div
              key={result.id || index}
              className={styles.resultItem}
              onClick={() => handleArticleClick(result.id)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {result.image && (
                <div className={styles.resultImage}>
                  <img
                    src={getImageUrl(result.image)}
                    alt={`${result.title} thumbnail`}
                    loading="lazy"
                    onError={() =>
                      console.error(
                        `Failed to load image for ${
                          result.title
                        }: ${getImageUrl(result.image)}`
                      )
                    }
                  />
                </div>
              )}
              <div className={styles.resultContent}>
                <span className={styles.category}>{result.category}</span>
                <h2>{result.title}</h2>
                <p>{result.description}</p>
                <div className={styles.authorInfo}>
                  {result.author.avatar ? (
                    <img
                      src={getImageUrl(result.author.avatar)}
                      alt={`${result.author.name} avatar`}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.authorPlaceholder}></div>
                  )}
                  <span className={styles.authorName}>
                    {result.author.name}
                  </span>
                  <span className={styles.date}>{result.date}</span>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <p className={styles.noResults}>
            No results found for "{searchQuery}"
          </p>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
